/**
 * chatArtifactTags — 채팅 AI 응답에 섞여 오는 학습지/PPT 제어 태그 파서.
 *
 * 백엔드 프롬프트가 내보내는 태그 문법(정확히 이 모양이어야 인식한다):
 *   [AUTO_GENERATE]                      아웃라인 새로 만들기 (텍스트 개요 즉시, 되묻기 시트 없음)
 *   [STYLE_UPDATE:디자인 설명]            아웃라인 유지, 디자인만 다시 만들기 (묻지 않고 바로)
 *   [SLIDE_EDIT:N:지시]                  N번(1부터) 슬라이드만 다시 만들기
 *   [ITEM_EDIT:N:지시]                   N번(1부터) 문항만 다시 만들기 → POST /api/outline/item-edit (없으면 기존 경로)
 *                                        `5~9` `5,6` 처럼 여러 번호면 [AUTO_GENERATE] 로 처리한다
 *   [PATCH:{"ops":[...]}]                JSON 패치 → POST /api/outline/patch. 중괄호/대괄호가 중첩되고
 *                                        문자열 안에 ']'가 들어갈 수 있어 정규식 대신 괄호 균형 스캔으로 잘라낸다.
 *   [EXPORT:hwpx|docx|pdf|pptx]           해당 포맷 파일 받기 트리거
 *   [MODE_GENERATE:image|text]            같은 초안의 반대 모드 산출물 (확인 UI 뒤 실행)
 *   [ARTIFACT_REF:대화ID:원본종류:만들종류]  다른 대화의 학습지/PPT를 참고해 새 산출물 만들기 (종류: worksheet|ppt)
 *                                        → 그 대화의 outline 을 reference_outline 으로 동봉해 대상 종류를 바로 생성
 *
 * 모든 함수는 순수 함수다. 화면 표시용 텍스트에서 태그를 지우는 stripArtifactTags도 여기서 제공한다.
 */

export const EXPORT_FORMATS = ['hwpx', 'docx', 'pdf', 'pptx'];

// 모델이 가끔 `[ AUTO_GENERATE ]` 처럼 괄호 안에 공백을 넣는다(2026-09 운영 리플레이) — 놓치면 태그가 화면에 그대로 보이고 초안이 안 만들어진다.
const AUTO_GENERATE_RE = /\[\s*AUTO_GENERATE\s*\]/;
const STYLE_UPDATE_RE = /\[STYLE_UPDATE:([^\]]+?)\]/;
// 모델이 한 번호 대신 `5~9` `5-9` `5,6` 같은 여러 번호를 적기도 한다(2026-10 운영) — 읽어서 다시 만들기로 돌린다.
const EDIT_NUMBERS = String.raw`\d+(?:\s*번)?(?:\s*(?:~|-|–|,|、|부터)\s*\d+(?:\s*번)?)*`;
const SLIDE_EDIT_RE = new RegExp(String.raw`\[SLIDE_EDIT:\s*(${EDIT_NUMBERS})\s*:([^\]]+)\]`);
const ITEM_EDIT_RE = new RegExp(String.raw`\[ITEM_EDIT:\s*(${EDIT_NUMBERS})\s*:([^\]]+)\]`);
const EDIT_TAG_GLOBAL_RE = new RegExp(String.raw`\[(?:SLIDE_EDIT|ITEM_EDIT):\s*${EDIT_NUMBERS}\s*:[^\]]+\]`, 'g');
const MAX_EDIT_NUMBERS = 50;

const parseEditNumbers = (spec) => {
  const numbers = [];
  const parts = String(spec || '').replace(/번/g, '').split(/\s*(?:,|、)\s*/);
  for (const part of parts) {
    const range = part.match(/^(\d+)\s*(?:~|-|–|부터)\s*(\d+)$/);
    if (range) {
      const from = parseInt(range[1], 10);
      const to = parseInt(range[2], 10);
      if (to < from || to - from >= MAX_EDIT_NUMBERS) return [];
      for (let n = from; n <= to; n += 1) numbers.push(n);
    } else if (/^\d+$/.test(part.trim())) {
      numbers.push(parseInt(part, 10));
    } else {
      return [];
    }
  }
  return [...new Set(numbers)].slice(0, MAX_EDIT_NUMBERS);
};
const EXPORT_RE = /\[EXPORT:\s*([A-Za-z]+)\s*\]/;
const MODE_GENERATE_RE = /\[MODE_GENERATE:\s*(image|text)\s*\]/i;
// 대화 ID는 Firestore 문서 ID(영숫자·-·_). 모델이 "ref:" 접두를 그대로 옮겨 적는 경우도 받아준다.
const ARTIFACT_REF_RE = /\[ARTIFACT_REF:\s*(?:ref:)?([A-Za-z0-9_-]{4,80})\s*:\s*(worksheet|ppt)\s*:\s*(worksheet|ppt)\s*\]/i;
const PATCH_OPEN = '[PATCH:';

export const ARTIFACT_REF_KINDS = ['worksheet', 'ppt'];

const parseArtifactRef = (match) => {
  if (!match) return null;
  const sourceKind = match[2].toLowerCase();
  const targetKind = match[3].toLowerCase();
  return { conversationId: match[1], sourceKind, targetKind };
};

const normalizeModeGenerate = (value) => {
  const mode = String(value || '').trim().toLowerCase();
  return mode === 'image' || mode === 'text' ? mode : null;
};

/**
 * text[start]가 '{' 또는 '['일 때 균형이 맞는 닫는 괄호 위치를 돌려준다. 문자열 리터럴 안의 괄호는 무시한다.
 * 균형이 맞지 않으면 -1.
 */
const findBalancedEnd = (text, start) => {
  const open = text[start];
  if (open !== '{' && open !== '[') return -1;
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = start; i < text.length; i += 1) {
    const ch = text[i];
    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
    } else if (ch === '{' || ch === '[') {
      depth += 1;
    } else if (ch === '}' || ch === ']') {
      depth -= 1;
      if (depth === 0) return i;
    }
  }
  return -1;
};

const CLOSER_OF = { '{': '}', '[': ']' };

/**
 * 괄호 짝이 한두 개 어긋난 JSON(모델이 `rows:[[...]]` 같은 깊은 중첩에서 `}` 하나를 빼먹거나 더 붙이는 경우)을
 * 괄호 종류별 스택으로 바로잡는다. 빠진 닫는 괄호는 채우고, 짝이 없는 닫는 괄호는 버린다.
 * 바깥 객체가 닫히지 않은 채 글이 끝나면(스트리밍 중) null.
 */
const repairBracketJson = (text, start) => {
  const stack = [];
  let out = '';
  let inString = false;
  let escaped = false;
  for (let i = start; i < text.length; i += 1) {
    const ch = text[i];
    if (inString) {
      out += ch;
      if (escaped) escaped = false;
      else if (ch === '\\') escaped = true;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') {
      inString = true;
      out += ch;
    } else if (ch === '{' || ch === '[') {
      stack.push(ch);
      out += ch;
    } else if (ch === '}' || ch === ']') {
      const opener = ch === '}' ? '{' : '[';
      const depth = stack.lastIndexOf(opener);
      if (depth < 0) continue;
      while (stack.length - 1 > depth) out += CLOSER_OF[stack.pop()];
      stack.pop();
      out += ch;
      if (stack.length === 0) return { json: out, end: i };
    } else {
      out += ch;
    }
  }
  return null;
};

const parsesAsJson = (json) => {
  try {
    JSON.parse(json);
    return true;
  } catch (_e) {
    return false;
  }
};

// 태그 바로 뒤에 붙은 짝 없는 `}` `]` 꼬리(모델이 괄호를 더 붙인 경우)는 태그와 함께 지운다.
const skipStrayClosers = (text, cursor) => {
  let end = cursor;
  while (end < text.length && (text[end] === ']' || text[end] === '}')) end += 1;
  return end === cursor || end === text.length || /\s/.test(text[end]) ? end : cursor;
};

const locateRepairedPatchTag = (text, start, jsonStart) => {
  const repaired = repairBracketJson(text, jsonStart);
  if (!repaired || !parsesAsJson(repaired.json)) return null;
  let cursor = repaired.end + 1;
  while (cursor < text.length && /[\s}]/.test(text[cursor])) cursor += 1;
  if (text[cursor] !== ']') return null;
  cursor = skipStrayClosers(text, cursor + 1);
  return { raw: text.slice(start, cursor), json: repaired.json, start, end: cursor, unterminated: false, repaired: true };
};

/**
 * [PATCH:{...}] 태그를 찾아 { raw, json, start, end, unterminated } 를 돌려준다. 없으면 null.
 * end는 닫는 ']' 다음 인덱스(slice용).
 */
export const locatePatchTag = (text) => {
  if (typeof text !== 'string') return null;
  const start = text.indexOf(PATCH_OPEN);
  if (start < 0) return null;
  const jsonStart = start + PATCH_OPEN.length;
  if (text[jsonStart] !== '{') {
    // 형식이 깨진 태그 — 닫는 ']'까지 통째로 잘라 화면에는 안 보이게 한다.
    const closeIdx = text.indexOf(']', jsonStart);
    return {
      raw: closeIdx >= 0 ? text.slice(start, closeIdx + 1) : text.slice(start),
      json: null,
      start,
      end: closeIdx >= 0 ? closeIdx + 1 : text.length,
      unterminated: closeIdx < 0,
    };
  }
  const jsonEnd = findBalancedEnd(text, jsonStart);
  if (jsonEnd < 0) {
    return locateRepairedPatchTag(text, start, jsonStart)
      || { raw: text.slice(start), json: null, start, end: text.length, unterminated: true };
  }
  // JSON 뒤에 공백이 있어도 닫는 ']'를 허용한다.
  let cursor = jsonEnd + 1;
  while (cursor < text.length && /\s/.test(text[cursor])) cursor += 1;
  const strictJson = text.slice(jsonStart, jsonEnd + 1);
  if (text[cursor] !== ']' || !parsesAsJson(strictJson)) {
    const repaired = locateRepairedPatchTag(text, start, jsonStart);
    if (repaired) return repaired;
  }
  if (text[cursor] !== ']') {
    return { raw: text.slice(start, jsonEnd + 1), json: strictJson, start, end: jsonEnd + 1, unterminated: false, malformed: true };
  }
  const end = skipStrayClosers(text, cursor + 1);
  return {
    raw: text.slice(start, end),
    json: strictJson,
    start,
    end,
    unterminated: false,
  };
};

const parsePatchOps = (json) => {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json);
    const ops = Array.isArray(parsed) ? parsed : parsed?.ops;
    if (!Array.isArray(ops) || ops.length === 0) return null;
    const valid = ops.every((op) => op && typeof op === 'object' && typeof op.op === 'string');
    return valid ? ops : null;
  } catch (_e) {
    return null;
  }
};

const normalizeExportFormat = (value) => {
  const format = String(value || '').trim().toLowerCase();
  return EXPORT_FORMATS.includes(format) ? format : null;
};

/**
 * 응답 전문에서 제어 태그를 모두 읽는다.
 * @returns {{
 *   autoGenerate: boolean,
 *   styleUpdate: string|null,
 *   slideEdit: {index:number, number:number, instruction:string}|null,   // index는 0부터
 *   itemEdit: {index:number, number:number, instruction:string}|null,
 *   patch: {ops:object[], raw:string}|null,
 *   exportFormat: 'hwpx'|'docx'|'pdf'|'pptx'|null,
 *   modeGenerate: 'image'|'text'|null,
 *   artifactRef: {conversationId:string, sourceKind:'worksheet'|'ppt', targetKind:'worksheet'|'ppt'}|null,
 *   hasAnyTag: boolean,
 * }}
 */
export const parseArtifactTags = (text) => {
  const source = typeof text === 'string' ? text : '';
  const styleMatch = source.match(STYLE_UPDATE_RE);
  const slideMatch = source.match(SLIDE_EDIT_RE);
  const itemMatch = source.match(ITEM_EDIT_RE);
  const exportMatch = source.match(EXPORT_RE);
  const modeMatch = source.match(MODE_GENERATE_RE);
  const refMatch = source.match(ARTIFACT_REF_RE);
  const patchTag = locatePatchTag(source);
  const patchOps = patchTag && !patchTag.unterminated ? parsePatchOps(patchTag.json) : null;

  const toEdit = (match) => {
    if (!match) return null;
    const numbers = parseEditNumbers(match[1]);
    const number = numbers[0];
    const instruction = match[2].trim();
    if (!Number.isFinite(number) || numbers.some((n) => n < 1) || !instruction) return null;
    const edit = { number, index: number - 1, instruction };
    if (numbers.length > 1) edit.numbers = numbers;
    return edit;
  };

  const result = {
    autoGenerate: AUTO_GENERATE_RE.test(source),
    styleUpdate: styleMatch ? styleMatch[1].trim() || null : null,
    slideEdit: toEdit(slideMatch),
    itemEdit: toEdit(itemMatch),
    patch: patchOps ? { ops: patchOps, raw: patchTag.raw } : null,
    exportFormat: exportMatch ? normalizeExportFormat(exportMatch[1]) : null,
    modeGenerate: modeMatch ? normalizeModeGenerate(modeMatch[1]) : null,
    artifactRef: parseArtifactRef(refMatch),
  };
  result.hasAnyTag = Boolean(
    result.autoGenerate || result.styleUpdate || result.slideEdit || result.itemEdit
    || result.patch || result.exportFormat || result.modeGenerate || result.artifactRef,
  );
  return result;
};

/**
 * [EXPORT:x] 가 지금 받을 수 있는 포맷(artifact_state.available_formats) 안에 있는지 확인한다.
 * availableFormats 가 배열이 아니면(미지정) 걸러내지 않는다. 백엔드도 rejected_exports 로 같은 필터를 하지만
 * 프롬프트가 태그를 흘려보낸 경우를 위한 이중 안전망이다.
 */
export const filterExportFormat = (format, availableFormats) => {
  if (!format) return null;
  if (!Array.isArray(availableFormats)) return format;
  const allowed = availableFormats.map((f) => String(f || '').trim().toLowerCase());
  return allowed.includes(format) ? format : null;
};

// 장·슬라이드·문항·섹션마다의 제목을 바꾸라는 요청. set_title(문서 전체 제목 하나)로는 표현할 수 없다.
// 표지(첫 장) 제목은 set_title 이 함께 바꾸므로 그대로 PATCH 로 둔다.
const ITEM_TITLE_REQUEST_RE = /(?:장|슬라이드|페이지|쪽|문항|문제|섹션|활동)\s*(?:들|마다|별)?\s*(?:의\s*)?(?:제목|타이틀)|소제목/;
const COVER_TITLE_ONLY_RE = /(?:^|[^\d])(?:1\s*번째?|1)\s*(?:장|슬라이드|페이지)|첫\s*(?:번째\s*)?(?:장|슬라이드|페이지)|표지/;
const PLURAL_CUE_RE = /모든|각|전부|모두|마다|별|들|다\s/;
const SLIDE_REF_RE = /(?:\d+|첫|두|세|네|다섯)\s*(?:번째\s*)?(?:장|슬라이드|페이지)/g;
const SLIDE_RANGE_RE = /\d+\s*(?:~|-|부터)\s*\d+\s*(?:번째\s*)?(?:장|슬라이드|페이지)/;
const namesSeveralSlides = (value) => (value.match(SLIDE_REF_RE) || []).length > 1 || SLIDE_RANGE_RE.test(value);
export const requestTargetsItemTitles = (text) => {
  const value = String(text || '');
  if (!ITEM_TITLE_REQUEST_RE.test(value)) return false;
  return !(COVER_TITLE_ONLY_RE.test(value) && !PLURAL_CUE_RE.test(value) && !namesSeveralSlides(value));
};
// "제목 글씨를 크게"처럼 제목의 모양을 바꾸는 요청은 디자인(STYLE_UPDATE)이 맞다.
const TITLE_STYLE_CUE_RE = /글씨|글꼴|폰트|크기|크게|작게|굵게|색|정렬|가운데|왼쪽|오른쪽|배치|위치|디자인|강조/;

/**
 * 태그들 사이의 우선순위를 한 곳에서 정한다.
 * PATCH(정확한 편집) > SLIDE/ITEM_EDIT(부분 재생성) > STYLE_UPDATE(디자인)
 *   > MODE_GENERATE(반대 모드) > AUTO_GENERATE(전체).
 * 학습지 PATCH 와 [MODE_GENERATE:image] 가 함께 오면 PATCH 뒤에 이미지형을 다시 그린다(payload.then).
 * EXPORT는 다른 행동과 함께 올 수 있어 별도 필드로 돌려준다.
 *
 * @param {ReturnType<typeof parseArtifactTags>} tags
 * @param {'ppt_maker'|'worksheet_gen'|string} category
 * @param {{ availableFormats?: string[]|null, userMessage?: string }} [options] availableFormats 를 주면 그 밖의 EXPORT 포맷은 무시한다.
 *   userMessage 는 이 답을 부른 교사 요청 — 장마다의 제목을 바꾸라는데 set_title 만 왔으면 다시 만들기로 돌린다
 * @returns {{ action: null|{type:string, payload?:object}, exportFormat: string|null }}
 */
export const resolveArtifactTagAction = (tags, category, { availableFormats = null, userMessage = '' } = {}) => {
  if (!tags) return { action: null, exportFormat: null };
  const isPpt = category === 'ppt_maker';
  const isWorksheet = category === 'worksheet_gen';
  let action = null;
  let patchOps = tags.patch?.ops || null;
  let titlesNeedRegenerate = false;
  if (patchOps && requestTargetsItemTitles(userMessage) && patchOps.some((op) => op?.op === 'set_title')) {
    // "모든 슬라이드 제목을 질문형으로" 에 모델이 set_title 을 붙이면 전체 제목만 바뀌고 장 제목은 그대로다 → 다시 만든다.
    patchOps = patchOps.filter((op) => op?.op !== 'set_title');
    titlesNeedRegenerate = patchOps.length === 0;
  }
  // 장 제목 문장을 바꾸라는데 STYLE_UPDATE 가 오면 디자인만 다시 그려 개요(제목)는 그대로 남는다 → 다시 만든다.
  if (!patchOps && tags.styleUpdate && !tags.slideEdit && !tags.itemEdit
    && requestTargetsItemTitles(userMessage) && !TITLE_STYLE_CUE_RE.test(String(userMessage || ''))) {
    titlesNeedRegenerate = true;
  }
  // 1장·1문항만 고치는 경로라서, 여러 번호가 오면 안내문 규칙("여러 개면 [AUTO_GENERATE]")대로 다시 만든다.
  const severalEditNumbers = Boolean((tags.slideEdit || tags.itemEdit)?.numbers);
  if ((titlesNeedRegenerate || (!patchOps?.length && severalEditNumbers)) && (isPpt || isWorksheet)) {
    action = { type: 'auto_generate' };
  } else if (patchOps && patchOps.length && (isPpt || isWorksheet)) {
    action = { type: 'patch', payload: { ops: patchOps } };
    // 디자인형 학습지 "2쪽으로", "글자 크게": 개요(쪽수·글자)를 고친 뒤 같은 디자인으로 다시 그린다.
    if (isWorksheet && tags.modeGenerate === 'image') action.payload.then = 'image';
  } else if ((tags.slideEdit || tags.itemEdit) && isPpt) {
    // PPT 프롬프트는 1장 수정에 [ITEM_EDIT:N:지시]를 우선 쓰게 한다 (SLIDE_EDIT 은 구버전 이름)
    action = { type: 'slide_edit', payload: tags.slideEdit || tags.itemEdit };
  } else if (tags.itemEdit && isWorksheet) {
    action = { type: 'item_edit', payload: tags.itemEdit };
  } else if (tags.styleUpdate && (isPpt || isWorksheet)) {
    action = { type: 'style_update', payload: { style: tags.styleUpdate } };
  } else if (tags.modeGenerate && (isPpt || isWorksheet)) {
    action = { type: 'mode_generate', payload: { mode: tags.modeGenerate } };
  } else if (tags.autoGenerate && (isPpt || isWorksheet)) {
    action = { type: 'auto_generate' };
  }
  return { action, exportFormat: filterExportFormat(tags.exportFormat || null, availableFormats) };
};

const FENCE_OPEN_RE = /^ {0,3}(`{3,}|~{3,})/;
const INLINE_CODE_RE = /(`+)[^`\n]+\1(?!`)/g;

const mapOutsideInlineCode = (text, fn) => {
  let out = '';
  let last = 0;
  text.replace(INLINE_CODE_RE, (match, _ticks, offset) => {
    out += fn(text.slice(last, offset)) + match;
    last = offset + match.length;
    return match;
  });
  return out + fn(text.slice(last));
};

/**
 * 코드펜스·인라인 코드 바깥 글에만 fn 을 적용한다. 닫히지 않은 펜스(스트리밍 중)는 끝까지 코드로 본다.
 * proseFenceLangs 에 든 펜스(예: wq-copy 복사용 글)는 안쪽도 글로 본다.
 */
const mapOutsideCode = (text, fn, { proseFenceLangs = null } = {}) => {
  const lines = text.split('\n');
  const pieces = [];
  let prose = [];
  let fence = null;
  let fenceBody = null;
  const flushProse = () => {
    if (prose.length) pieces.push(mapOutsideInlineCode(prose.join('\n'), fn));
    prose = [];
  };
  lines.forEach((line) => {
    if (fence) {
      const close = line.match(FENCE_OPEN_RE);
      if (close && close[1][0] === fence[0] && close[1].length >= fence.length && !line.slice(close[0].length).trim()) {
        fence = null;
        if (fenceBody && fenceBody.length) pieces.push(fn(fenceBody.join('\n')));
        fenceBody = null;
        pieces.push(line);
        return;
      }
      if (fenceBody) fenceBody.push(line);
      else pieces.push(line);
      return;
    }
    const open = line.match(FENCE_OPEN_RE);
    if (open && !(open[1][0] === '`' && line.slice(open[0].length).includes('`'))) {
      flushProse();
      fence = open[1];
      const lang = line.slice(open[0].length).trim().split(/\s+/)[0].toLowerCase();
      fenceBody = proseFenceLangs && proseFenceLangs.includes(lang) ? [] : null;
      pieces.push(line);
      return;
    }
    prose.push(line);
  });
  if (fenceBody && fenceBody.length) pieces.push(fn(fenceBody.join('\n')));
  flushProse();
  return pieces.join('\n');
};

const stripEmptyCitationParensInProse = (text) => text
  .replace(/[ \t]*\(\{sources\}\)/gi, '')
  .replace(/[ \t]*\(근거:\s*\{[^}]*\}\s*\)/g, '')
  .replace(/[ \t]*\(근거:\s*\)/g, '')
  .replace(/[ \t]*\(\s*\)/g, '');

/** 값이 비어 있는 근거/출처 괄호 자리를 지운다. `()` `(근거: )` `({sources})` — 코드 속 `()` 는 그대로 둔다. */
export const stripEmptyCitationParens = (text) => {
  if (!text || typeof text !== 'string') return text;
  if (!text.includes('`') && !text.includes('~~~')) return stripEmptyCitationParensInProse(text);
  return mapOutsideCode(text, stripEmptyCitationParensInProse);
};

// OpenAI 웹 검색 출처 표시가 본문에 새어 나온 것: `\ue200cite\ue202turn0search0\ue201`(사용자 영역 문자 U+E200~),
// 그 문자만 빠진 `citeturn0search0turn1news2`, 낱개 `turn0search0`, 파일 검색식 `【4†source】`.
const LEAK_HINT_RE = /[\ue200-\ue2ff]|turn\d|【\d/;
const PUA_SEQUENCE_RE = /[ \t]*\ue200([a-z_]+)\ue202([^\ue200\ue201]*)\ue201/g;
const PUA_UNTERMINATED_RE = /[ \t]*\ue200[^\ue201]{0,200}$/;
const PUA_CHAR_RE = /[\ue200-\ue2ff]/g;
const TURN_TOKEN = String.raw`turn\d+(?:search|news|view|fetch|image|file|forecast|finance|sports)\d+`;
const PLAIN_CITE_RE = new RegExp(String.raw`[ \t]*(?:filecite|cite)?(?:${TURN_TOKEN})+`, 'g');
const FILE_CITE_RE = /[ \t]*【\d+(?::\d+)?†[^】\n]{0,80}】/g;
const URL_SPAN_RE = /https?:\/\/[^\s<>()[\]]+/g;

// `entity` 표시는 안에 든 이름(두 번째 값)만 남긴다. 나머지(cite·navlist 등)는 통째로 지운다.
const puaSequenceText = (kind, payload) => {
  if (kind !== 'entity') return '';
  try {
    const parsed = JSON.parse(payload);
    return Array.isArray(parsed) && typeof parsed[1] === 'string' ? ` ${parsed[1]}` : '';
  } catch (_e) {
    return '';
  }
};

const stripCitationLeaksOutsideUrls = (text) => text
  .replace(PLAIN_CITE_RE, '')
  .replace(FILE_CITE_RE, '');

const stripCitationLeaksInProse = (text) => {
  const withoutPua = text
    .replace(PUA_SEQUENCE_RE, (match, kind, payload) => puaSequenceText(kind, payload))
    .replace(PUA_UNTERMINATED_RE, '')
    .replace(PUA_CHAR_RE, '');
  if (!/turn\d|【\d/.test(withoutPua)) return withoutPua;
  let out = '';
  let last = 0;
  withoutPua.replace(URL_SPAN_RE, (url, offset) => {
    out += stripCitationLeaksOutsideUrls(withoutPua.slice(last, offset)) + url;
    last = offset + url.length;
    return url;
  });
  return out + stripCitationLeaksOutsideUrls(withoutPua.slice(last));
};

/**
 * 새어 나온 출처 표시(사용자 영역 문자·`citeturn0search0`·`【4†source】`)를 지운다.
 * 코드펜스·인라인 코드·주소 안은 그대로 두고, wq-copy 복사용 글 펜스 안은 지운다.
 */
export const stripLeakedCitationMarkers = (text) => {
  if (!text || typeof text !== 'string' || !LEAK_HINT_RE.test(text)) return text;
  if (!text.includes('`') && !text.includes('~~~')) return stripCitationLeaksInProse(text);
  return mapOutsideCode(text, stripCitationLeaksInProse, { proseFenceLangs: ['wq-copy'] });
};

/**
 * 화면 표시용 텍스트에서 제어 태그를 지운다. 스트리밍 중 아직 닫히지 않은 태그도 뒤에서부터 잘라낸다.
 */
export const stripArtifactTags = (text) => {
  if (!text || typeof text !== 'string') return text;
  let out = text;
  // PATCH는 괄호 균형 스캔으로 (여러 개 있을 수 있어 반복)
  for (let guard = 0; guard < 10; guard += 1) {
    const tag = locatePatchTag(out);
    if (!tag) break;
    out = out.slice(0, tag.start) + out.slice(tag.end);
  }
  out = out
    .replace(/\[\s*AUTO_GENERATE\s*\]/g, '')
    .replace(/\[STYLE_UPDATE:[^\]]*\]/g, '')
    .replace(EDIT_TAG_GLOBAL_RE, '')
    .replace(/\[EXPORT:\s*[A-Za-z]+\s*\]/g, '')
    .replace(/\[MODE_GENERATE:\s*(?:image|text)\s*\]/gi, '')
    .replace(/\[ARTIFACT_REF:[^\]]*\]/gi, '')
    // 스트리밍 중 아직 닫히지 않은 태그 꼬리
    .replace(/\[\s*(?:AUTO_GENERATE|STYLE_UPDATE|SLIDE_EDIT|ITEM_EDIT|EXPORT|MODE_GENERATE|ARTIFACT_REF)(?::[^\]]*)?\s*$/g, '');
  return stripEmptyCitationParens(out);
};

const chatArtifactTags = {
  EXPORT_FORMATS,
  filterExportFormat,
  locatePatchTag,
  parseArtifactTags,
  resolveArtifactTagAction,
  stripArtifactTags,
  stripEmptyCitationParens,
  stripLeakedCitationMarkers,
};

export default chatArtifactTags;

/**
 * AI 답에 태그가 없을 때 쪽수·문항 수 휴리스틱 폴백. 쪽수 맞춤은 set_layout PATCH 이고,
 * 지금 결과가 디자인형(이미지)이면 고친 구성으로 같은 디자인을 다시 그린다(payload.then).
 */
export const countIntentFallbackAction = (countIntent, latestRecord) => {
  if (countIntent?.requestKind !== 'worksheet_fit') return { type: 'auto_generate' };
  const payload = { ops: [{ op: 'set_layout', layout_hint: { target_pages: countIntent.target } }] };
  if (latestRecord?.sourceMode === 'image') payload.then = 'image';
  return { type: 'patch', payload };
};
